import { AsyncPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
  OnInit,
  output,
  runInInjectionContext,
  Signal,
  viewChild,
} from '@angular/core';
import { outputFromObservable, takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  Observable,
  pairwise,
  shareReplay,
  skip,
  startWith,
  take,
} from 'rxjs';
import { map } from 'rxjs/operators';
import {
  EFieldTypes,
  ESqlOperators,
  EWidget,
  TFilterSave,
  TFiltersWithType,
  DateUtilitiesService as WidgetDateUtilities,
} from 'scw-digital-boards-widgets';
import { TrimmedRequiredValidator } from '../../../../core/validators/trimmed-required.validator';
import { DateUtilitiesService, rangeNameToDate } from '../../../../utilities/date-utilities.service';
import { DateRangePickerComponent } from '../../date-range-picker/date-range-picker.component';
import { IDateRangePickerDate } from '../../date-range-picker/date-range-picker.interface';
import { ErrorMessagesComponent } from '../../error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../mat-dropdown/scw-mat-dropdown.interface';
import { AdvancedFilterPredefinedData } from '../advanced-filter-predefined-data';
import {
  IAdvancedFilterApiValueConfig,
  IAdvancedFilterCustomApiConfig,
  IAdvancedFilterCustomPredefinedConfig,
  IAdvancedFilterPredefinedConfig,
  IFilterIdentity,
} from '../advanced-filter.interface';
import { AdvancedFilterService } from '../advanced-filter.service';
import {
  EAdvancedFilterInputMode,
  IDataForWidgetSpecificConfiguration,
  TAdvancedFilterInputsAutoSubmit,
  TModalConfiguration,
} from './advanced-filter-inputs.interface';

@Component({
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDropdown,
    TranslatePipe,
    MatFormField,
    MatInput,
    MatLabel,
    MatFormField,
    ErrorMessagesComponent,
    MatButton,
    MatDatepickerModule,
    MatFormFieldModule,
    DateRangePickerComponent,
    MatIcon,
    MatMiniFabButton,
    AsyncPipe,
  ],
  providers: [AdvancedFilterService],
  selector: 'advanced-filter-inputs',
  styleUrl: './advanced-filter-inputs.component.scss',
  templateUrl: './advanced-filter-inputs.component.html',
})
export class AdvancedFilterInputsComponent<Widget extends EWidget> implements OnInit {
  private readonly operatorDropdown = viewChild<MatDropdown>('operatorDropdown');
  private readonly valueDropdown = viewChild<MatDropdown>('valueDropdown');

  public readonly dataForWidgetSpecificConfiguration =
    input.required<null | IDataForWidgetSpecificConfiguration<Widget>>();
  public readonly inputsConfiguration = input.required<TModalConfiguration>();
  public readonly isOptional = input(false);
  public readonly isFormSubmitted = input(false);
  public readonly disabled = input(false);
  public readonly selectedFilterIdentity = model<IFilterIdentity | null>(null);
  public readonly submitClick = output<TFilterSave>();
  public readonly deleteClick = output();
  public readonly dismissClick = output();

  public readonly translate = inject(TranslateService);
  private readonly advancedFilterPredefinedData = inject(AdvancedFilterPredefinedData);
  private readonly advancedFilterService = inject(AdvancedFilterService);
  private readonly injector = inject(Injector);
  private readonly widgetDateUtilities = inject(WidgetDateUtilities);

  private readonly advancedFilterConfiguration = computed(() => {
    const dataForWidgetSpecificConfiguration = this.dataForWidgetSpecificConfiguration();

    return dataForWidgetSpecificConfiguration
      ? this.advancedFilterService.getConfigurationsOfWidget(
          dataForWidgetSpecificConfiguration,
          this.inputsConfiguration().isEqAllowed,
        )
      : this.advancedFilterService.manager.configuration();
  });

  private readonly advancedFilterConfiguration$ = toObservable(this.advancedFilterConfiguration);
  public readonly fieldDropdownOptions = computed(() => {
    try {
      return this.advancedFilterConfiguration()
        .filter(({ field }) => {
          const { fixedField } = this.inputsConfiguration();

          return !fixedField || fixedField === field;
        })
        .map(({ field, idOfCustom }) => ({
          id: field,
          name: this.advancedFilterService.getFieldName(field, idOfCustom),
        }));
    } catch (_) {
      return [];
    }
  });

  private readonly fieldDropdownOptions$ = toObservable(this.fieldDropdownOptions);

  public readonly fieldDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: true,
    hasMatError: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.field'),
  };

  public readonly operatorDropdownConfiguration: IDropdownSettings = {
    ...this.fieldDropdownConfiguration,
    text: this.translate.instant('field.operator'),
  };

  public readonly formGroup = new FormGroup({
    apiDropdownValue: new FormControl<IDropdownOption<string | number | boolean>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    dateRange: new FormControl<IDateRangePickerDate | null>(null, { validators: [Validators.required] }),
    field: new FormControl<IDropdownOption<string>[]>([], { nonNullable: true, validators: [Validators.required] }),
    numberValue: new FormControl<number | null>(null, { validators: [Validators.required] }),
    operator: new FormControl<IDropdownOption<ESqlOperators>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    stringValue: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator()],
    }),
  });

  public readonly autoSubmit$ = this.formGroup.valueChanges.pipe(startWith(this.formGroup.value)).pipe(
    map((): TAdvancedFilterInputsAutoSubmit => {
      return this.isFormValid() ? { data: this.formatOutput(), isValid: true } : { data: null, isValid: false };
    }),
  );

  public readonly autoSubmit = outputFromObservable<TAdvancedFilterInputsAutoSubmit>(this.autoSubmit$);

  private readonly selectedFieldOption$ = this.formGroup.controls.field.valueChanges.pipe(
    startWith(this.formGroup.controls.field.value),
    map(() => {
      return this.formGroup.controls.field.value.length ? this.formGroup.controls.field.value[0].id : null;
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly operatorDropdownOptions$: Observable<IDropdownOption<ESqlOperators>[]> = combineLatest({
    advancedFilterConfiguration: this.advancedFilterConfiguration$,
    selectedField: this.selectedFieldOption$,
  }).pipe(
    map(({ selectedField, advancedFilterConfiguration }) => {
      const matchingConfiguration = advancedFilterConfiguration.find(({ field }) => field === selectedField);

      return !matchingConfiguration
        ? []
        : matchingConfiguration.configurations.reduce((reducePayload, operator) => {
            return [
              ...reducePayload,
              ...operator.operators.map((op) => ({
                id: op,
                name: this.advancedFilterService.operatorNames[op],
              })),
            ];
          }, [] as IDropdownOption<ESqlOperators>[]);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly operatorDropdownOptions = toSignal(this.operatorDropdownOptions$, { initialValue: [] });

  public readonly selectedFieldConfiguration$ = this.selectedFieldOption$.pipe(
    map((selectedField) => {
      return !selectedField
        ? undefined
        : this.advancedFilterConfiguration().find(({ field }) => field === selectedField);
    }),
  );

  public readonly selectedFieldConfiguration = toSignal(this.selectedFieldConfiguration$);

  public readonly selectedOperatorConfiguration$ = combineLatest({
    selectedFieldConfiguration: this.selectedFieldConfiguration$,
    selectedOperatorOption: this.formGroup.controls.operator.valueChanges.pipe(
      startWith(this.formGroup.controls.operator.value),
    ),
  }).pipe(
    map(({ selectedOperatorOption, selectedFieldConfiguration }) => {
      if (!selectedOperatorOption?.length || !selectedFieldConfiguration) {
        return undefined;
      }

      return selectedFieldConfiguration.configurations.find(({ operators }) =>
        (operators as ESqlOperators[]).includes(selectedOperatorOption[0].id),
      );
    }),
  );

  public readonly selectedOperatorConfiguration = toSignal(this.selectedOperatorConfiguration$);
  private readonly isSelectedOperatorApiDropdownWithServerSideSearch = computed(() => {
    const operatorConfiguration = this.selectedOperatorConfiguration();

    return operatorConfiguration?.type === EFieldTypes.apiDropdown && !operatorConfiguration.clientSideSearch;
  });
  public readonly apiDropdownConfiguration: Signal<IDropdownSettings> = computed(() => {
    const selectedOperatorConfiguration = this.selectedOperatorConfiguration();
    const commonFields: Partial<IDropdownSettings> = {
      ...this.fieldDropdownConfiguration,
      enableCheckAll:
        selectedOperatorConfiguration?.type !== EFieldTypes.apiDropdown ||
        Boolean(selectedOperatorConfiguration.clientSideSearch),
      hasDelayedDefaultValue: true,
      isClientSide: !this.isSelectedOperatorApiDropdownWithServerSideSearch(),
      singleSelection: this.selectedFilterIdentity()?.operator !== ESqlOperators.$in,
    };

    try {
      return {
        ...commonFields,
        text: this.isOptional() ? '' : this.translate.instant('field.value'),
      };
    } catch {
      return {
        ...commonFields,
        text: this.translate.instant('field.value'),
      };
    }
  });
  public readonly valueOptions = computed(() => {
    const operatorConfiguration = this.selectedOperatorConfiguration();

    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (operatorConfiguration?.type) {
      case EFieldTypes.apiDropdown:
      case EFieldTypes.customApiDropdown:
        return operatorConfiguration.resource.formattedData();
      case EFieldTypes.predefined:
        return this.advancedFilterPredefinedData.getOption(operatorConfiguration.dataKey);
      case EFieldTypes.customPredefined:
        return operatorConfiguration.values;
      default:
        return [];
    }
  });

  public readonly EFieldTypes = EFieldTypes;
  public readonly EAdvancedFilterInputMode = EAdvancedFilterInputMode;

  constructor() {
    effect(() => {
      const operatorConfiguration = this.selectedOperatorConfiguration();

      if (
        operatorConfiguration?.type === EFieldTypes.apiDropdown ||
        operatorConfiguration?.type === EFieldTypes.customApiDropdown
      ) {
        operatorConfiguration.resource.trigger.set(true);
      }
    });

    this.setFormControlDisabledStatus();

    effect(() => {
      if (this.isFormSubmitted()) {
        this.formGroup.updateValueAndValidity();
      }
    });

    effect(() => {
      if (this.isOptional()) {
        this.formGroup.controls.apiDropdownValue.clearValidators();
        this.formGroup.controls.stringValue.clearValidators();
        this.formGroup.controls.numberValue.clearValidators();
        this.formGroup.controls.dateRange.clearValidators();
        this.formGroup.controls.operator.clearValidators();
        this.formGroup.controls.field.clearValidators();

        return;
      }

      this.formGroup.controls.apiDropdownValue.setValidators([Validators.required]);
      this.formGroup.controls.stringValue.setValidators([Validators.required, TrimmedRequiredValidator()]);
      this.formGroup.controls.numberValue.setValidators([Validators.required]);
      this.formGroup.controls.dateRange.setValidators([Validators.required]);
      this.formGroup.controls.operator.setValidators([Validators.required]);
      this.formGroup.controls.field.setValidators([Validators.required]);
    });
    this.onFieldChangeSubscription();
    this.onOperatorChangeSubscription();
    this.fieldDropdownOptions$.pipe(filter((options) => options.length === 1)).subscribe((options) => {
      this.formGroup.controls.field.reset([options[0]]);
    });

    this.selectedOperatorConfiguration$.subscribe((operatorConfiguration) => {
      const field = this.formGroup.controls.field.value?.[0]?.id;
      const operator = this.formGroup.controls.operator.value?.[0]?.id;

      this.selectedFilterIdentity.set(
        field && operator
          ? {
              field,
              operator,
              ...(operatorConfiguration && 'idOfCustom' in operatorConfiguration
                ? { idOfCustom: operatorConfiguration.idOfCustom }
                : {}),
            }
          : null,
      );

      if (
        this.inputsConfiguration().type === 'add' &&
        this.inputsConfiguration().mode === EAdvancedFilterInputMode.autoSubmitWithCancel &&
        (operatorConfiguration?.type === EFieldTypes.apiDropdown ||
          operatorConfiguration?.type === EFieldTypes.predefined ||
          operatorConfiguration?.type === EFieldTypes.customPredefined ||
          operatorConfiguration?.type === EFieldTypes.customApiDropdown)
      ) {
        setTimeout(() => this.valueDropdown()?.openDropdown(), 100);
      }
    });
  }

  public ngOnInit(): void {
    this.assignInitialValues();
    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this.inputsConfiguration().fixedField) {
          this.formGroup.controls.field.disable();
        } else {
          this.formGroup.controls.field.enable();
        }
      });
    });
  }

  public submit(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.submitClick.emit(this.formatOutput());
  }

  private formatOutput(): TFiltersWithType {
    const selectedOperatorConfiguration = this.selectedOperatorConfiguration();

    if (!selectedOperatorConfiguration) {
      throw 'formatOutput is called on an invalid form';
    }

    const basis = {
      field: this.formGroup.controls.field.value[0].id,
      operator: this.formGroup.controls.operator.value[0].id,
    };

    switch (selectedOperatorConfiguration.type) {
      case EFieldTypes.apiDropdown:
      case EFieldTypes.predefined:
      case EFieldTypes.customPredefined:
      case EFieldTypes.customApiDropdown:
        return {
          ...basis,
          type: selectedOperatorConfiguration.type,
          value: this.extractValueFromDropdown(),
          ...(selectedOperatorConfiguration.type === EFieldTypes.customPredefined ||
          selectedOperatorConfiguration.type === EFieldTypes.customApiDropdown
            ? { idOfCustom: selectedOperatorConfiguration.idOfCustom }
            : {}),
        } as TFiltersWithType;
      case EFieldTypes.number:
        return {
          ...basis,
          type: selectedOperatorConfiguration.type,
          value: this.formGroup.controls.numberValue.value!,
        } as TFiltersWithType;
      case EFieldTypes.string:
        return {
          ...basis,
          type: selectedOperatorConfiguration.type,
          value: this.formGroup.controls.stringValue.value!.trim(),
        } as TFiltersWithType;
      case EFieldTypes.date:
        const predefinedEquivalentIfAny = this.widgetDateUtilities.getPredefinedEquivalentIfAny(
          this.formGroup.controls.dateRange.value?.startDate?.format('YYYY-MM-DD') ?? '',
          this.formGroup.controls.dateRange.value?.endDate?.format('YYYY-MM-DD') ?? '',
          Boolean(
            this.formGroup.controls.operator.value.length &&
              this.formGroup.controls.operator.value[0].id !== ESqlOperators.$between,
          ),
        );

        if (predefinedEquivalentIfAny) {
          return {
            ...basis,
            operator: ESqlOperators.$period,
            type: EFieldTypes.predefined,
            value: predefinedEquivalentIfAny,
          } as TFiltersWithType;
        } else if (basis.operator === ESqlOperators.$between) {
          return {
            ...basis,
            type: selectedOperatorConfiguration.type,
            value: {
              endDate: this.formGroup.controls.dateRange.value?.endDate?.format('YYYY-MM-DD'),
              startDate: this.formGroup.controls.dateRange.value?.startDate?.format('YYYY-MM-DD'),
            },
          } as TFiltersWithType;
        } else {
          return {
            ...basis,
            type: selectedOperatorConfiguration.type,
            value: dayjs(this.formGroup.controls.dateRange.value!.startDate).format('YYYY-MM-DD'),
          } as TFiltersWithType;
        }
      case EFieldTypes.boolean:
      case undefined:
        return { ...basis, type: selectedOperatorConfiguration.type } as TFiltersWithType;
      default:
        throw 'unhandled advanced filter output';
    }
  }

  public dropdownSearch(search: string): void {
    if (!this.isSelectedOperatorApiDropdownWithServerSideSearch()) {
      return;
    }

    (this.selectedOperatorConfiguration() as IAdvancedFilterApiValueConfig).resource.commonParameters.search.set(
      search,
    );
  }

  public dismissOrDelete(): void {
    if (this.isFormValid()) {
      this.dismissClick.emit();
    } else {
      this.deleteClick.emit();
    }
  }

  private extractValueFromDropdown<T extends string | number | boolean = number>(): T | T[] {
    const operator = this.formGroup.controls.operator.value[0].id;
    const apiDropdownValue = this.formGroup.controls.apiDropdownValue.value as IDropdownOption<T>[];

    return operator === ESqlOperators.$in ? apiDropdownValue.map(({ id }) => id) : apiDropdownValue[0].id;
  }

  private assignInitialValues(): void {
    const modalConfiguration = this.inputsConfiguration();

    if (modalConfiguration.type !== 'edit') {
      const initialField = modalConfiguration.fixedField ?? modalConfiguration.initialField;

      if (initialField) {
        this.formGroup.controls.field.reset(this.fieldDropdownOptions().filter(({ id }) => id === initialField));
      }

      return;
    }

    const { initialValues } = modalConfiguration;

    this.formGroup.controls.field.reset(
      this.fieldDropdownOptions().filter(
        ({ id }) => id === this.advancedFilterService.getFieldId(initialValues.field, initialValues.idOfCustom),
      ),
    );

    const readinessCondition = [this.fieldDropdownOptions$.pipe(filter((options) => Boolean(options.length)))];

    if (this.inputsConfiguration().type === 'edit') {
      readinessCondition.push(this.operatorDropdownOptions$.pipe(filter((options) => Boolean(options.length))));
    }

    combineLatest(readinessCondition)
      .pipe(take(1))
      .subscribe(() => {
        this.setInitialOperatorAndValue(initialValues);

        this.selectedFieldOption$.pipe(skip(1)).subscribe(() => {
          this.formGroup.controls.apiDropdownValue.reset([]);
        });
      });
  }

  private setInitialOperatorAndValue(initialValues: TFilterSave): void {
    this.formGroup.controls.operator.reset(
      this.operatorDropdownOptions().filter(
        ({ id }) =>
          id === initialValues.operator ||
          (id === ESqlOperators.$eq &&
            [ESqlOperators.$in, ESqlOperators.$between, ESqlOperators.$period].includes(initialValues.operator)) ||
          (id === ESqlOperators.$between && initialValues.operator === ESqlOperators.$period),
      ),
    );

    const configuration = this.selectedOperatorConfiguration()!;

    switch (configuration.type) {
      case EFieldTypes.apiDropdown:
      case EFieldTypes.customApiDropdown:
        this.assignInitialApiDropdownValue(initialValues, configuration.operators);

        return;
      case EFieldTypes.predefined:
      case EFieldTypes.customPredefined:
        this.assignInitialPredefinedDropdownValue(configuration, initialValues);

        return;
      case EFieldTypes.boolean:
        return;
      case EFieldTypes.number:
        this.formGroup.controls.numberValue.setValue(initialValues.value as number);

        return;
      case EFieldTypes.string:
        this.formGroup.controls.stringValue.setValue(initialValues.value as string);
        break;
      case EFieldTypes.date:
        if (initialValues.operator === ESqlOperators.$between) {
          if (
            initialValues.operator === ESqlOperators.$between &&
            !configuration.operators.includes(ESqlOperators.$between) &&
            initialValues.value.startDate !== initialValues.value.endDate
          ) {
            // If the widget works with a single date but the values provided in $between is not a single date, don't use the value
            return;
          }

          this.formGroup.controls.dateRange.setValue({
            endDate: DateUtilitiesService.buildDayjsFromISO8601DateString(
              (initialValues.value as IDateRangePickerDate<string>).endDate!,
            ),
            startDate: DateUtilitiesService.buildDayjsFromISO8601DateString(
              (initialValues.value as IDateRangePickerDate<string>).startDate!,
            ),
          });

          break;
        }

        if (initialValues.operator === ESqlOperators.$period) {
          if (
            configuration.operators.includes(ESqlOperators.$eq) &&
            initialValues.value !== 'today' &&
            initialValues.value !== 'yesterday'
          ) {
            return;
          }

          this.formGroup.controls.dateRange.setValue(rangeNameToDate[initialValues.value]());

          break;
        }

        this.formGroup.controls.dateRange.setValue({
          endDate: DateUtilitiesService.buildDayjsFromISO8601DateString(initialValues.value as string),
          startDate: DateUtilitiesService.buildDayjsFromISO8601DateString(initialValues.value as string),
        });

        break;
    }
  }

  private assignInitialPredefinedDropdownValue(
    configuration: IAdvancedFilterPredefinedConfig | IAdvancedFilterCustomPredefinedConfig,
    initialValues: TFilterSave,
  ): void {
    if (
      initialValues.operator === ESqlOperators.$in &&
      !configuration.operators.includes(ESqlOperators.$in) &&
      Array.isArray(initialValues.value) &&
      initialValues.value.length > 1
    ) {
      // If the widget works with eq but the values provided in $in is more than one, don't use them
      return;
    }

    const options =
      configuration.type === EFieldTypes.predefined
        ? this.advancedFilterPredefinedData.getOption(configuration.dataKey)
        : configuration.values;

    if (initialValues.operator === ESqlOperators.$in || initialValues.operator === ESqlOperators.$eq) {
      const initialValueArray = Array.isArray(initialValues.value)
        ? (initialValues.value as number[])
        : [initialValues.value as number];

      this.formGroup.controls.apiDropdownValue.setValue(
        initialValueArray.map((initialValue) => {
          return options.find(({ id }) => id === initialValue) ?? { id: initialValue, name: '...' };
        }),
      );
    } else if (initialValues.operator === ESqlOperators.$period) {
      this.formGroup.controls.dateRange.setValue(rangeNameToDate[initialValues.value]());
    }
  }

  private assignInitialApiDropdownValue(initialValues: TFilterSave, configurationOperators: ESqlOperators[]): void {
    if (
      initialValues.operator === ESqlOperators.$in &&
      !configurationOperators.includes(ESqlOperators.$in) &&
      Array.isArray(initialValues.value) &&
      initialValues.value.length > 1
    ) {
      // If the widget works with eq but the values provided in $in is more than one, don't use them
      return;
    }

    if (initialValues.operator === ESqlOperators.$in) {
      this.formGroup.controls.apiDropdownValue.setValue(
        (initialValues.value as number[]).map((id) => ({ id, name: '...' })),
      );
    } else {
      this.formGroup.controls.apiDropdownValue.setValue([{ id: initialValues.value as number, name: '...' }]);
    }

    (
      this.selectedOperatorConfiguration() as IAdvancedFilterApiValueConfig | IAdvancedFilterCustomApiConfig
    ).resource.commonParameters.initialIds.set(
      this.formGroup.controls.apiDropdownValue.value.map(({ id }) => id as number),
    );
  }

  private isFormValid(): boolean {
    if (!this.formGroup.controls.field.value.length || !this.formGroup.controls.operator.value.length) {
      return false;
    }

    switch (this.selectedOperatorConfiguration()?.type) {
      case EFieldTypes.apiDropdown:
      case EFieldTypes.predefined:
      case EFieldTypes.customPredefined:
      case EFieldTypes.customApiDropdown:
        return Boolean(this.formGroup.controls.apiDropdownValue.value.length);
      case EFieldTypes.string:
        return Boolean(this.formGroup.controls.stringValue.value);
      case EFieldTypes.number:
        return this.formGroup.controls.numberValue.value === 0 || Boolean(this.formGroup.controls.numberValue.value);
      case EFieldTypes.date:
        return (
          Boolean(this.formGroup.controls.dateRange.value?.startDate) &&
          Boolean(this.formGroup.controls.dateRange.value?.endDate)
        );
      case undefined:
        return false;
      case EFieldTypes.boolean:
        return true;
    }
  }

  private onFieldChangeSubscription(): void {
    // Changing field causes the value of operatorDropdownOptions$ to update
    this.operatorDropdownOptions$.pipe(takeUntilDestroyed()).subscribe((options) => {
      // Auto select if there is only one available option
      if (options.length === 1) {
        this.formGroup.controls.operator.reset([options[0]]);

        return;
      }

      if (
        this.inputsConfiguration().type === 'add' &&
        this.inputsConfiguration().mode === EAdvancedFilterInputMode.autoSubmitWithCancel
      ) {
        setTimeout(() => this.operatorDropdown()?.openDropdown(), 100);
      }

      // If the new field don't contain the previously selected operator, discard the value
      if (
        options.length &&
        this.formGroup.controls.operator.value.length &&
        !options.some(({ id }) => id === this.formGroup.controls.operator.value[0].id)
      ) {
        this.formGroup.controls.operator.reset([]);
      }
    });

    this.formGroup.controls.field.valueChanges
      .pipe(
        takeUntilDestroyed(),
        startWith(this.formGroup.controls.field.value, this.formGroup.controls.field.value),
        pairwise(),
        filter(
          ([oldValue, newValue]) =>
            (!oldValue?.length && !oldValue?.length) || (Boolean(oldValue?.length) && !isEqual(oldValue, newValue)),
        ),
      )
      .subscribe(() => {
        this.formGroup.controls.dateRange.reset(null);
        this.formGroup.controls.apiDropdownValue.reset([]);
        this.formGroup.controls.numberValue.reset(null);
        this.formGroup.controls.stringValue.reset('');
      });
  }

  private onOperatorChangeSubscription(): void {
    this.formGroup.controls.operator.valueChanges
      .pipe(
        takeUntilDestroyed(),
        startWith(this.formGroup.controls.operator.value, this.formGroup.controls.operator.value),
        pairwise(),
        filter(
          ([oldValue, newValue]) =>
            (!oldValue?.length && !oldValue?.length) || (Boolean(oldValue?.length) && !isEqual(oldValue, newValue)),
        ),
      )
      .subscribe(() => {
        this.formGroup.controls.dateRange.reset(null);
        this.formGroup.controls.apiDropdownValue.reset([]);
        this.formGroup.controls.numberValue.reset(null);
        this.formGroup.controls.stringValue.reset('');
      });
  }

  private setFormControlDisabledStatus(): void {
    effect(() => {
      if (this.selectedFieldConfiguration()) {
        this.formGroup.controls.operator.enable();
      } else {
        this.formGroup.controls.operator.disable();
      }
    });

    effect(() => {
      if (this.disabled()) {
        this.formGroup.disable();
      } else {
        this.formGroup.enable();
      }
    });
  }
}
